// Real performance instrumentation for Stage 0, per the v6 architecture
// doc's performance strategy: measure, don't guess. This is a foundation,
// not a full observability stack — it captures frontend startup timing and
// every API call's client-observed duration, and best-effort-beacons them
// to /api/telemetry, which currently just logs structured JSON server-side
// (Cloudflare Workers Logs / `wrangler tail`). Swapping the sink for a real
// analytics backend later does not require changing any call site here.

import { appConfig } from './env';

export interface ApiTimingEvent {
  type: 'api_call';
  action: string;
  method: string;
  durationMs: number;
  ok: boolean;
  status: number;
  serverDurationMs?: number;
  at: string;
}

export interface StartupTimingEvent {
  type: 'startup';
  metric: 'app_shell_rendered' | 'first_route_ready';
  durationMs: number;
  at: string;
}

export type TelemetryEvent = ApiTimingEvent | StartupTimingEvent;

const queue: TelemetryEvent[] = [];
let flushScheduled = false;

function scheduleFlush() {
  if (flushScheduled) return;
  flushScheduled = true;
  // Batch bursts of events (e.g. several API calls firing together) into one
  // request instead of one fetch per event.
  setTimeout(flush, 1500);
}

function flush() {
  flushScheduled = false;
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  const body = JSON.stringify({ environment: appConfig.environment, events: batch });
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(`${appConfig.apiBaseUrl}/telemetry`, blob);
      return;
    }
  } catch {
    // fall through to fetch
  }
  fetch(`${appConfig.apiBaseUrl}/telemetry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // Telemetry must never surface an error to the user or block the UI.
  });
}

export function recordEvent(event: TelemetryEvent) {
  queue.push(event);
  scheduleFlush();
}

export function recordStartupMetric(metric: StartupTimingEvent['metric'], durationMs: number) {
  recordEvent({ type: 'startup', metric, durationMs, at: new Date().toISOString() });
}

/** Wrap an API call to record its client-observed duration regardless of outcome. */
export async function timedFetch(
  action: string,
  method: string,
  run: () => Promise<Response>,
): Promise<Response> {
  const start = performance.now();
  let response: Response | undefined;
  try {
    response = await run();
    return response;
  } finally {
    const durationMs = Math.round(performance.now() - start);
    const serverDurationHeader = response?.headers.get('X-Server-Duration-Ms');
    recordEvent({
      type: 'api_call',
      action,
      method,
      durationMs,
      ok: response?.ok ?? false,
      status: response?.status ?? 0,
      serverDurationMs: serverDurationHeader ? Number(serverDurationHeader) : undefined,
      at: new Date().toISOString(),
    });
  }
}

/** Marks navigation-timing-derived startup metrics once, shortly after the app shell mounts. */
export function recordNavigationStartupOnce() {
  if (typeof performance === 'undefined' || !performance.getEntriesByType) return;
  const [nav] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  if (nav) {
    recordStartupMetric('app_shell_rendered', Math.round(nav.domContentLoadedEventEnd));
  }
}
